/**
 * useMediaPermissions Hook
 * Main hook for permission lifecycle management
 * Handles permission checking, requesting, and recovery flows
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  PERMISSION_STATES,
  REQUEST_RESULTS,
  MEDIA_ERROR_CODES,
  getPermissionStatus,
  checkMediaPermissions,
  checkDevicesAvailable,
  monitorPermissionChanges,
  detectPermissionViaGetUserMedia,
  generateRecoveryPlan,
  shouldShowEducation,
  getEducationContent,
  detectBrowser,
} from 'src/services/mediaPermissions'
import { useBrowserDetection } from './useBrowserDetection'
import { usePermissionPersistence } from './usePermissionPersistence'

export const useMediaPermissions = (options = {}) => {
  const {
    mediaType = 'both', // 'camera', 'microphone', 'both'
    autoCheck = true, // Automatically check on mount
    sessionType = 'general', // For tracking different session types
    onPermissionChange = null, // Callback when permissions change
  } = options

  // Browser detection
  const browserInfo = useBrowserDetection()

  // Permission persistence
  const persistence = usePermissionPersistence(sessionType)

  // Permission states
  const [cameraPermission, setCameraPermission] = useState(PERMISSION_STATES.UNKNOWN)
  const [microphonePermission, setMicrophonePermission] = useState(PERMISSION_STATES.UNKNOWN)
  const [isChecking, setIsChecking] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [hasDevices, setHasDevices] = useState({ camera: true, microphone: true })
  const [error, setError] = useState(null)

  // UI flow states
  const [showEducation, setShowEducation] = useState(false)
  const [showBlockedDialog, setShowBlockedDialog] = useState(false)
  const [recoveryPlan, setRecoveryPlan] = useState(null)
  const [requestAttempts, setRequestAttempts] = useState(0)

  // Refs for cleanup
  const monitorCleanupRef = useRef(null)
  const streamsRef = useRef([])

  // Computed states
  const needsCamera = mediaType === 'camera' || mediaType === 'both'
  const needsMicrophone = mediaType === 'microphone' || mediaType === 'both'

  const isGranted =
    (needsCamera ? cameraPermission === PERMISSION_STATES.GRANTED : true) &&
    (needsMicrophone ? microphonePermission === PERMISSION_STATES.GRANTED : true)

  const isDenied =
    (needsCamera && cameraPermission === PERMISSION_STATES.DENIED) ||
    (needsMicrophone && microphonePermission === PERMISSION_STATES.DENIED)

  const isPrompt =
    (needsCamera && cameraPermission === PERMISSION_STATES.PROMPT) ||
    (needsMicrophone && microphonePermission === PERMISSION_STATES.PROMPT)

  // Check permission status
  const checkPermissions = useCallback(async () => {
    if (isChecking) return

    setIsChecking(true)
    setError(null)

    try {
      // Check devices first
      const devicesInfo = await checkDevicesAvailable()
      setHasDevices({
        camera: devicesInfo.hasCamera,
        microphone: devicesInfo.hasMicrophone,
      })

      // Check permission states
      const permissions = await checkMediaPermissions()
      
      if (needsCamera) {
        setCameraPermission(permissions.camera)
      }
      if (needsMicrophone) {
        setMicrophonePermission(permissions.microphone)
      }

      // Auto-trigger education flow if permissions are in PROMPT state
      // This ensures the permission dialog shows up automatically when page loads
      const isPrompt = 
        (needsCamera && permissions.camera === PERMISSION_STATES.PROMPT) ||
        (needsMicrophone && permissions.microphone === PERMISSION_STATES.PROMPT)
      
      if (isPrompt && shouldShowEducation(sessionType)) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => {
          setShowEducation(true)
        }, 100) // Small delay to ensure state updates are complete
      }

      persistence.updateCheckTime()

      return {
        camera: permissions.camera,
        microphone: permissions.microphone,
        devices: devicesInfo,
      }
    } catch (err) {
      console.error('Error checking permissions:', err)
      setError(err.message)
      return null
    } finally {
      setIsChecking(false)
    }
  }, [isChecking, needsCamera, needsMicrophone, persistence, sessionType])

  // Request permissions
  const requestPermissions = useCallback(
    async (skipEducation = false) => {
      if (isRequesting) return { result: REQUEST_RESULTS.ERROR, message: 'Already requesting' }

      setIsRequesting(true)
      setError(null)

      try {
        // Build constraints
        const constraints = {}
        if (needsCamera && hasDevices.camera) constraints.video = true
        if (needsMicrophone && hasDevices.microphone) constraints.audio = true

        if (Object.keys(constraints).length === 0) {
          throw new Error('No devices available')
        }

        // Request access
        const stream = await navigator.mediaDevices.getUserMedia(constraints)

        // Store stream reference for cleanup
        streamsRef.current.push(stream)

        // Update states
        if (needsCamera && hasDevices.camera) {
          setCameraPermission(PERMISSION_STATES.GRANTED)
        }
        if (needsMicrophone && hasDevices.microphone) {
          setMicrophonePermission(PERMISSION_STATES.GRANTED)
        }

        // Log success
        persistence.addEntry({
          mediaType,
          result: REQUEST_RESULTS.SUCCESS,
          browser: browserInfo.browser,
          device: browserInfo.deviceType,
        })

        // Callback
        if (onPermissionChange) {
          onPermissionChange({
            camera: needsCamera ? PERMISSION_STATES.GRANTED : null,
            microphone: needsMicrophone ? PERMISSION_STATES.GRANTED : null,
          })
        }

        return {
          result: REQUEST_RESULTS.SUCCESS,
          stream,
        }
      } catch (err) {
        console.error('Error requesting permissions:', err)

        let result = REQUEST_RESULTS.ERROR
        let message = err.message

        // Categorize error
        if (err.name === MEDIA_ERROR_CODES.NOT_ALLOWED) {
          result = REQUEST_RESULTS.DENIED
          message = 'Permission denied by user'

          // Update states to denied
          if (needsCamera) setCameraPermission(PERMISSION_STATES.DENIED)
          if (needsMicrophone) setMicrophonePermission(PERMISSION_STATES.DENIED)

          // Generate recovery plan
          const plan = generateRecoveryPlan(mediaType, true)
          setRecoveryPlan(plan)
          setShowBlockedDialog(true)
        } else if (err.name === MEDIA_ERROR_CODES.NOT_FOUND) {
          result = REQUEST_RESULTS.DEVICE_NOT_FOUND
          message = 'No camera or microphone found'
        } else if (err.name === MEDIA_ERROR_CODES.NOT_READABLE) {
          result = REQUEST_RESULTS.ALREADY_IN_USE
          message = 'Device is already in use'
        }

        setError(message)
        setRequestAttempts((prev) => prev + 1)

        // Log failure
        persistence.addEntry({
          mediaType,
          result,
          error: err.name,
          browser: browserInfo.browser,
          device: browserInfo.deviceType,
        })

        return {
          result,
          message,
          error: err,
        }
      } finally {
        setIsRequesting(false)
      }
    },
    [
      isRequesting,
      needsCamera,
      needsMicrophone,
      hasDevices,
      mediaType,
      browserInfo,
      persistence,
      onPermissionChange,
    ],
  )

  // Start the permission flow (with education)
  const startPermissionFlow = useCallback(() => {
    // Always show education as per plan requirement
    const educationContent = getEducationContent(mediaType)
    setShowEducation(true)
  }, [mediaType])

  // Proceed after education
  const proceedAfterEducation = useCallback(() => {
    setShowEducation(false)
    persistence.markEducationComplete()
    return requestPermissions(true)
  }, [requestPermissions, persistence])

  // Retry after user fixes permissions
  const retryPermissions = useCallback(async () => {
    try {
      setShowBlockedDialog(false)
      setRecoveryPlan(null)
      
      // Re-check permissions with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Permission check timeout')), 5000)
      )
      
      const checkPromise = checkPermissions()
      
      await Promise.race([checkPromise, timeoutPromise])
      
      // Small delay for Safari
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // If still denied, show blocked dialog again
      const status = await getPermissionStatus(mediaType)
      if (
        status.permissions.camera === PERMISSION_STATES.DENIED ||
        status.permissions.microphone === PERMISSION_STATES.DENIED
      ) {
        const plan = generateRecoveryPlan(mediaType, true)
        setRecoveryPlan(plan)
        setShowBlockedDialog(true)
      } else if (
        status.permissions.camera === PERMISSION_STATES.PROMPT ||
        status.permissions.microphone === PERMISSION_STATES.PROMPT
      ) {
        // Try requesting again
        return requestPermissions(true)
      } else if (
        status.permissions.camera === PERMISSION_STATES.GRANTED &&
        status.permissions.microphone === PERMISSION_STATES.GRANTED
      ) {
        // Success! Trigger onSuccess callback
        if (onPermissionChange) {
          onPermissionChange({
            camera: PERMISSION_STATES.GRANTED,
            microphone: PERMISSION_STATES.GRANTED,
          })
        }
      }
    } catch (error) {
      console.error('Error retrying permissions:', error)
      // Show alert for Safari users
      if (detectBrowser() === 'safari') {
        alert('Please refresh the page after changing Safari settings.')
      }
      setShowBlockedDialog(false)
    }
  }, [checkPermissions, mediaType, requestPermissions, onPermissionChange])

  // Cleanup streams
  const cleanupStreams = useCallback(() => {
    streamsRef.current.forEach((stream) => {
      stream.getTracks().forEach((track) => track.stop())
    })
    streamsRef.current = []
  }, [])

  // Monitor permission changes
  // CRITICAL: Only monitor if autoCheck is true
  // For offline sessions, skip monitoring to prevent permission queries
  useEffect(() => {
    if (!autoCheck) {
      // Explicitly skip monitoring for offline sessions
      return
    }

    const cleanup = monitorPermissionChanges((change) => {
      if (change.type === 'camera' && needsCamera) {
        setCameraPermission(change.state)
      }
      if (change.type === 'microphone' && needsMicrophone) {
        setMicrophonePermission(change.state)
      }

      if (onPermissionChange) {
        onPermissionChange(change)
      }
    })

    monitorCleanupRef.current = cleanup

    return () => {
      if (monitorCleanupRef.current) {
        monitorCleanupRef.current()
      }
    }
  }, [autoCheck, needsCamera, needsMicrophone, onPermissionChange])

  // Initial permission check
  // CRITICAL: Only check permissions if autoCheck is true
  // For offline sessions, autoCheck will be false, so this won't run
  // Use ref for checkPermissions to avoid dependency loop
  const checkPermissionsRef = useRef(checkPermissions)
  useEffect(() => {
    checkPermissionsRef.current = checkPermissions
  }, [checkPermissions])
  
  useEffect(() => {
    if (autoCheck && !browserInfo.isLoading) {
      checkPermissionsRef.current()
    }
    // Explicitly do nothing if autoCheck is false to prevent any permission queries
  }, [autoCheck, browserInfo.isLoading])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStreams()
      if (monitorCleanupRef.current) {
        monitorCleanupRef.current()
      }
    }
  }, [cleanupStreams])

  return {
    // Permission states
    cameraPermission,
    microphonePermission,
    isGranted,
    isDenied,
    isPrompt,
    isChecking,
    isRequesting,
    hasDevices,
    error,

    // Browser info
    browserInfo,

    // UI states
    showEducation,
    showBlockedDialog,
    recoveryPlan,
    requestAttempts,

    // Actions
    checkPermissions,
    requestPermissions,
    startPermissionFlow,
    proceedAfterEducation,
    retryPermissions,
    cleanupStreams,

    // UI controls
    setShowEducation,
    setShowBlockedDialog,

    // Persistence
    permissionHistory: persistence.history,
    persistentIssues: persistence.persistentIssues,
  }
}

export default useMediaPermissions

